/**
 * Mint a reCAPTCHA v3 token in the browser, outside React.
 *
 * /api/auth/send-code refuses a code to a request with no token (since
 * 2026-10-18), so every caller has to bring one: the login modal through
 * useRecaptcha, and the guest checkout, guest sell and email capture sends
 * through this. A token is single-use and lives two minutes, so mint it right
 * before the request that spends it.
 */
import { RECAPTCHA_SITE_KEY, recaptchaClient, type RecaptchaClient } from './recaptcha-client';
import { trackError, ErrorSeverity } from './error-tracking';

/**
 * How long a send waits for reCAPTCHA to finish loading before it goes on
 * without a token.
 */
export const RECAPTCHA_READY_TIMEOUT_MS = 5000;

type GrecaptchaGlobal = RecaptchaClient;

/**
 * api.js defines `grecaptcha.ready` as soon as it runs, but `execute` only
 * arrives once it has fetched its second bundle from gstatic. A click in that
 * gap called an undefined `execute` ("window.grecaptcha.execute is not a
 * function") and sent the code with no token. `ready` queues the callback
 * until `execute` exists, which is the order Google documents.
 */
const whyExecuteUnavailable = (grecaptcha: GrecaptchaGlobal): Promise<string | null> => {
	if (typeof grecaptcha.execute === 'function') return Promise.resolve(null);
	const ready = grecaptcha.ready;
	if (typeof ready !== 'function') return Promise.resolve('no grecaptcha.ready to wait on');
	return new Promise((resolve) => {
		const timer = setTimeout(() => resolve(`ready did not fire within ${RECAPTCHA_READY_TIMEOUT_MS}ms`), RECAPTCHA_READY_TIMEOUT_MS);
		ready(() => {
			clearTimeout(timer);
			resolve(typeof grecaptcha.execute === 'function' ? null : 'still missing after ready fired');
		});
	});
};

/** Every failure here is the same call site to the error log. */
const CONTEXT = { action: 'executeRecaptcha', component: 'useRecaptcha' };

/** A fresh token for `action`, or undefined when reCAPTCHA is not there. Never throws. */
export async function getRecaptchaToken(action: string = 'login'): Promise<string | undefined> {
	try {
		const grecaptcha = recaptchaClient();
		if (!grecaptcha) {
			trackError(new Error('grecaptcha not loaded'), ErrorSeverity.ERROR, CONTEXT);
			return undefined;
		}

		const unavailable = await whyExecuteUnavailable(grecaptcha);
		if (unavailable || !grecaptcha.execute) {
			trackError(new Error(`grecaptcha.execute unavailable: ${unavailable}`), ErrorSeverity.ERROR, CONTEXT);
			return undefined;
		}

		return await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
	} catch (e) {
		trackError(e as Error | string, ErrorSeverity.ERROR, CONTEXT);
		return undefined;
	}
}
